import { type ApolloClient, gql } from '@apollo/client/core';
import type { Mutation, Query, SaveUserMutationInput, UserAttributes } from '../../../generated/api.graphql';
import { ACCOUNT_AGNOSTIC } from '../../accountContext';
import * as VPErrors from '../../../VPError';
import { take } from '../../../util';
import { VP_ERROR_FIELDS } from '../../VPError/fragments';
import type { VPResult } from '../../VPResult';
import * as AccountConfig from './AccountConfig';
import * as Me from './Me';
import * as User from './User';

/**
 * The AppSchema replacement for the legacy `app { users(userId:) }` self-lookup, which the
 * backend deprecates with "Self-lookup moved to currentUser on AppSchema". It takes no
 * arguments — there is only ever one answer, the caller's own profile.
 *
 * It carries the account configuration too, which the legacy interface asked for separately
 * under `app { accountConfig }`. One document rather than two selections on the same field is
 * what keeps the profile read and the account read from writing conflicting partial copies of
 * the same normalized `Me` entity. They still cost a round trip each, because the two callers
 * never overlap: the account read happens once at boot from the app root, the profile read only
 * when the profile page mounts.
 *
 * The selection is inline rather than a fragment: used once, exactly as
 * `Internal/CurrentUser/Api.ts` does for the same field on the internal schema.
 */
export const CURRENT_USER_QUERY = gql`
  query currentUser {
    currentUser {
      id
      name
      email
      accountConfig {
        name
        features {
          feature
        }
      }
    }
  }
`;

export const fetchCurrentUser = async (client: ApolloClient<Query>): Promise<User.User> => {
  const { data } = await client.query<Query>({
    query: CURRENT_USER_QUERY,
    fetchPolicy: 'network-only',
  });

  // Resolving undefined would leave the profile form rendering against a half-populated
  // model with nothing to say the read had failed.
  if (!data?.currentUser) {
    throw new Error('currentUser query returned no user');
  }

  return Me.fromGql(data.currentUser);
};

export const SESSION_PROBE_OPERATION = 'sessionProbe';

// Only the operator is selected, so unlike `CURRENT_USER_QUERY` it needs no account.
export const SESSION_QUERY = gql`
  query sessionProbe {
    currentUser {
      id
    }
  }
`;

/** Whether the backend still recognises the caller; AppSchema answers `null` for a dead session. */
export const hasSession = async (client: ApolloClient<unknown>): Promise<boolean> => {
  const { data } = await client.query<Query>({ query: SESSION_QUERY, fetchPolicy: 'network-only', context: ACCOUNT_AGNOSTIC });

  return !!data?.currentUser;
};

/**
 * The account configuration, off the same document. `Me.accountConfig` is nullable, and
 * resolving undefined here would leave every `hasFeature()` call silently false — the whole app
 * rendered as though the account had bought nothing, with nothing to say the read had failed.
 */
export const fetchAccountConfig = async (
  client: ApolloClient<Query>,
): Promise<AccountConfig.AccountConfig> => {
  const { data } = await client.query<Query>({
    query: CURRENT_USER_QUERY,
    fetchPolicy: 'network-only',
  });

  if (!data?.currentUser?.accountConfig) {
    throw new Error('currentUser query returned no account config');
  }

  return AccountConfig.fromGql(data.currentUser.accountConfig);
};

/**
 * The same `saveUser`, lifted out of the legacy `app { }` wrapper to the mutation root.
 *
 * Its payload carries no `success` field — unlike `saveEvent`'s — so success is derived
 * here from the errors and the user it did or did not return.
 */
export const SAVE_USER_MUTATION = gql`
  mutation saveUser($input: SaveUserMutationInput!) {
    saveUser(input: $input) {
      user {
        id
        name
        email
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${VP_ERROR_FIELDS}
`;

export const saveUser = async (
  client: ApolloClient<Mutation>,
  user: User.User,
): Promise<VPResult<User.User, VPErrors.VPError[]>> => {
  // `UserAttributes` accepts exactly these three, so whitelist rather than forward the
  // domain object: a field added to it later must not start failing the whole mutation on
  // an unknown argument. The type argument does not police the key list — the spec does.
  const input: SaveUserMutationInput = { user: take<UserAttributes>(user, 'id', 'name', 'email') };

  const { data } = await client.mutate<Mutation>({ mutation: SAVE_USER_MUTATION, variables: { input } });

  const payload = data?.saveUser;

  if (!payload) {
    throw new Error('saveUser mutation returned no data');
  }

  if (payload.errors?.length) {
    return { success: false, errors: VPErrors.fromGqlCollection(payload.errors) };
  }

  // Neither branch is answerable from here: reporting success would hand the form an
  // undefined to reset itself from, reporting failure an empty but truthy error list.
  if (!payload.user) {
    throw new Error('saveUser returned neither a user nor errors');
  }

  return { success: true, data: User.fromGql(payload.user) };
};
